import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Menu, X, Check, ArrowRight, MessageCircle,
  Fuel, Calendar, Gauge, Star, ChevronRight,
  Car, Quote, Image, Lock, MapPin, ShieldCheck, Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhatsAppGroupButton, useWhatsAppGroupUrl } from "@/components/WhatsAppGroupButton";
import { SmartImage } from "@/components/SmartImage";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { CinematicHero } from "@/components/home/CinematicHero";
import { CinematicStory } from "@/components/home/CinematicStory";
import { TrustCounters } from "@/components/home/TrustCounters";
import { FeatureCarousel } from "@/components/home/FeatureCarousel";
import { SectionHeading, Reveal, LightBeams } from "@/components/home/FX";
import "@/styles/home.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const waGroupUrl = useWhatsAppGroupUrl();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cars, setCars] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [formData, setFormData] = useState({
    name: "", phone: "", budget: "", brand: "", model: "",
    year: "", mileage: "", fuel: "", transmission: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    fetchCars();
    fetchReviews();
  }, []);

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      setCars(response.data);
    } catch (error) {
      setCars([]);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      setReviews([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      setFormSubmitted(true);
      toast.success("Köszönjük érdeklődését!", { duration: 5000 });
      setFormData({ name: "", phone: "", budget: "", brand: "", model: "", year: "", mileage: "", fuel: "", transmission: "", message: "" });
      setTimeout(() => setFormSubmitted(false), 5000);
    } catch (error) {
      toast.error("Hiba történt");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const shareCar = async (e, car) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/car/${car.id}`;
    const title = `${car.brand} ${car.model}`;
    const text = `${car.brand} ${car.model} (${car.year}) - ${car.price} — AlpineCar`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link vágólapra másolva");
      }
    } catch (err) { /* user cancelled share */ }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const target = el.getBoundingClientRect().top + window.scrollY - 72;
    const start = window.scrollY;
    const dist = target - start;
    const dur = Math.min(1200, Math.max(500, Math.abs(dist) * 0.25));
    const t0 = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      window.scrollTo(0, start + dist * ease(p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    setTimeout(() => window.scrollTo(0, target), dur + 150);
    setMobileMenuOpen(false);
  };

  const defaultReviews = [
    { name: "Kovács Péter", location: "Budapest", rating: 5, text: "Kiváló szolgáltatás! Az autó pontosan olyan állapotban érkezett, ahogy leírták.", car_purchased: "VW Passat" },
    { name: "Nagy András", location: "Debrecen", rating: 5, text: "Professzionális csapat. A teljes ügyintézést elintézték helyettem.", car_purchased: "Skoda Octavia" },
    { name: "Szabó Gergely", location: "Győr", rating: 5, text: "Átlátható folyamat, mindent előre megbeszéltünk. Ajánlom mindenkinek.", car_purchased: "Ford Transit" },
    { name: "Tóth Viktória", location: "Szeged", rating: 5, text: "Végig tartották a kapcsolatot, minden kérdésemre válaszoltak.", car_purchased: "Toyota Yaris" }
  ];

  const displayReviews = reviews.length > 0 ? reviews : defaultReviews;

  const COUNTRY_FLAGS = { "Ausztria": "🇦🇹", "Magyarország": "🇭🇺", "Németország": "🇩🇪" };

  const navItems = [
    { id: "cars", label: "Autók" },
    { id: "process", label: "Folyamat" },
    { id: "trust", label: "Rólunk" },
  ];

  return (
    <div className="home-root min-h-screen">
      <div className="home-grain" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050505]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="/" className="flex items-center group">
              <img src="/alpine-logo-full.png" alt="Alpine Car Austria" className="h-12 sm:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
            </a>

            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navItems.map((s) => (
                <button key={s.id} onClick={() => scrollToSection(s.id)} data-testid={`nav-${s.id}`} className="relative text-sm text-white/70 hover:text-white transition-colors duration-200 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#D4AF37] after:transition-[width] after:duration-300 hover:after:w-full">
                  {s.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection("search")}
                className="btn-gold rounded-full font-semibold px-6 py-2.5 text-sm"
                data-testid="search-cars-nav"
              >
                Autót keresek
              </button>
            </div>

            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && createPortal(
          <div className="fixed inset-0 lg:hidden" style={{ backgroundColor: '#000', zIndex: 99999 }}>
            <div className="flex flex-col h-full bg-black">
              <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
                <img src="/alpine-logo-full.png" alt="Alpine Car" className="h-10 w-auto object-contain" />
                <button onClick={() => setMobileMenuOpen(false)} className="p-2"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex flex-col items-center justify-center flex-1 gap-6">
                {[...navItems, { id: "search", label: "Autót keresek" }].map((s) => (
                  <button key={s.id} onClick={() => scrollToSection(s.id)} className="text-xl font-medium">
                    {s.label}
                  </button>
                ))}
                <Button onClick={() => scrollToSection("cars")} className="btn-gold rounded-full px-8 py-4 text-lg mt-4">
                  Elérhető autók
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </nav>

      {/* Cinematic Hero */}
      <CinematicHero onBrowse={() => scrollToSection("cars")} />

      {/* Cinematic sourcing story — fullscreen scroll story */}
      <CinematicStory onBrowse={() => scrollToSection("cars")} />

      {/* Cars — vehicle catalogue */}
      <section id="cars" className="relative py-20 sm:py-32 bg-[#070708] overflow-hidden">
        <LightBeams />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <SectionHeading
            eyebrow="Aktuális kínálat"
            title="Elérhető"
            titleAccent="autók"
            sub="Ausztriából származó, gondosan kiválasztott és átvizsgált járművek."
          />
          <Reveal className="mb-12 sm:mb-16">
            <div className="gold-divider" />
          </Reveal>

          {cars.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {cars.map((car, i) => (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0, y: 44 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link to={`/car/${car.id}`} className="premium-card shine group block rounded-2xl overflow-hidden h-full">
                    <div className="relative aspect-[16/10] overflow-hidden bg-[#151517]">
                      {car.images?.[0] ? (
                        <SmartImage
                          src={car.images[0]}
                          variant="med"
                          srcset
                          sizes="(max-width: 768px) 50vw, (max-width: 1280px) 33vw, 25vw"
                          alt={`${car.brand} ${car.model}`}
                          className="w-full h-full transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20">
                          <Image className="w-12 h-12" />
                        </div>
                      )}
                      {car.sold && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="bg-red-600 text-white font-bold text-lg sm:text-xl px-6 py-2 rounded-lg -rotate-12 shadow-xl border-2 border-red-400" data-testid="sold-badge">
                            ELADVA
                          </div>
                        </div>
                      )}
                      {!car.sold && car.status === "reserved" && (
                        <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex items-center justify-center" data-testid="reserved-overlay">
                          <div className="bg-amber-500 text-black font-bold text-base sm:text-lg px-5 py-1.5 rounded-lg -rotate-6 shadow-xl border-2 border-amber-300 flex items-center gap-1.5">
                            <Lock className="w-4 h-4" /> FOGLALT
                          </div>
                        </div>
                      )}
                      <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 backdrop-blur-sm rounded-lg text-right">
                        <span className="text-[#D4AF37] font-bold text-sm block">{car.price}</span>
                      </div>
                      {!car.sold && car.status !== "reserved" && (car.available_immediately || car.available_short_term) && (
                        <div className="absolute top-3 left-3">
                          <AvailabilityBadge car={car} size="sm" />
                        </div>
                      )}
                    </div>
                    <div className="p-4 sm:p-5">
                      <h3 className="font-display font-semibold text-base sm:text-lg mb-1">{car.brand} {car.model} {car.engine && <span className="text-white/60 font-normal text-sm">{car.engine}</span>}</h3>
                      <div className="flex flex-wrap gap-3 mt-3 text-xs sm:text-sm text-white/50">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{car.year}</span>
                        <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{car.mileage}</span>
                        <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" />{car.fuel}</span>
                        {car.origin_country && <span className="flex items-center gap-1">{COUNTRY_FLAGS[car.origin_country]} {car.origin_country}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                        <span className="text-xs text-[#D4AF37]">Megnézem az autót</span>
                        <div className="flex items-center gap-1">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => shareCar(e, car)}
                            onKeyDown={(e) => { if (e.key === "Enter") shareCar(e, car); }}
                            aria-label="Autó megosztása"
                            data-testid={`share-car-${car.id}`}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                          >
                            <Share2 className="w-4 h-4" />
                          </span>
                          <ChevronRight className="w-4 h-4 text-[#D4AF37] transition-transform duration-300 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <Reveal>
              <div className="text-center py-16 premium-card rounded-2xl">
                <Car className="w-12 h-12 mx-auto text-white/20 mb-4" />
                <p className="text-white/50 mb-2">Jelenleg nincs elérhető autó</p>
                <p className="text-sm text-white/30">Kérjen egyedi keresést!</p>
                <Button onClick={() => scrollToSection("search")} className="btn-gold rounded-full mt-4 px-8">
                  Autót keresek
                </Button>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Trust counters */}
      <TrustCounters />

      {/* Feature carousel — inspection & administration value */}
      <FeatureCarousel />

      {/* Search Form */}
      <section id="search" className="relative py-20 sm:py-32 bg-[#050505] overflow-hidden">
        <LightBeams variant="red" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <SectionHeading
            eyebrow="Egyedi keresés"
            title="Milyen autót"
            titleAccent="keres?"
            sub="Írja le, és megkeressük Önnek a megfelelő, ausztriai járművet."
          />

          <Reveal>
            <div className="premium-card rounded-2xl sm:rounded-3xl p-5 sm:p-8">
              {formSubmitted ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-500/15 rounded-full flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-2">Köszönjük!</h3>
                  <p className="text-white/60 text-sm">Hamarosan felvesszük Önnel a kapcsolatot.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Név *" required className="home-input" />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Telefon *" required className="home-input" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <input type="text" name="brand" value={formData.brand} onChange={handleChange} placeholder="Márka *" required className="home-input" />
                    <input type="text" name="model" value={formData.model} onChange={handleChange} placeholder="Típus" className="home-input" />
                    <input type="text" name="budget" value={formData.budget} onChange={handleChange} placeholder="Keret (€) *" required className="home-input" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <input type="text" name="year" value={formData.year} onChange={handleChange} placeholder="Év" className="home-input" />
                    <input type="text" name="mileage" value={formData.mileage} onChange={handleChange} placeholder="Max km" className="home-input" />
                    <select name="fuel" value={formData.fuel} onChange={handleChange} className="home-input">
                      <option value="" className="bg-[#0A0A0A]">Üzemanyag</option>
                      <option value="Benzin" className="bg-[#0A0A0A]">Benzin</option>
                      <option value="Dízel" className="bg-[#0A0A0A]">Dízel</option>
                      <option value="Hibrid" className="bg-[#0A0A0A]">Hibrid</option>
                      <option value="Elektromos" className="bg-[#0A0A0A]">Elektromos</option>
                    </select>
                    <select name="transmission" value={formData.transmission} onChange={handleChange} className="home-input">
                      <option value="" className="bg-[#0A0A0A]">Váltó</option>
                      <option value="Automata" className="bg-[#0A0A0A]">Automata</option>
                      <option value="Manuális" className="bg-[#0A0A0A]">Manuális</option>
                    </select>
                  </div>
                  <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Egyéb megjegyzés..." rows={2} className="home-input resize-none" />
                  <Button type="submit" disabled={isSubmitting} className="w-full btn-gold rounded-full font-semibold py-5 sm:py-6 text-sm sm:text-base">
                    {isSubmitting ? "Küldés..." : "Keresés indítása"} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="relative py-20 sm:py-32 bg-[#070708]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <SectionHeading eyebrow="Vélemények" title="Ügyfeleink" titleAccent="mondták" />

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {displayReviews.slice(0, 4).map((review, i) => (
              <Reveal key={i} delay={(i % 2) * 0.12}>
                <div className="premium-card shine rounded-2xl p-5 sm:p-6 h-full" data-testid={`review-card-${i}`}>
                  <div className="flex gap-1 mb-3">
                    {[...Array(review.rating || 5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                  <Quote className="w-6 h-6 text-[#D4AF37]/30 mb-2" />
                  <p className="text-white/70 text-sm leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{review.name}</div>
                      <div className="text-xs text-white/40">{review.location}</div>
                    </div>
                    {review.car_purchased && (
                      <span className="text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded-full">{review.car_purchased}</span>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing statement */}
      <section className="relative py-24 sm:py-36 bg-[#050505] overflow-hidden" data-testid="closing-section">
        <div className="absolute inset-0">
          <img src="/closing-bg.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#050505]/78" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/45 to-[#050505]/70" />
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.07), transparent 60%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-[11px] sm:text-xs font-bold tracking-[0.35em] uppercase text-[#D4AF37] mb-6">
              <span className="w-8 h-px bg-[#D4AF37]/60" /> Ausztriából Magyarországra <span className="w-8 h-px bg-[#D4AF37]/60" />
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              Ausztriából Magyarországra.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="font-display text-2xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#F0D77B] via-[#D4AF37] to-[#8f6f1d] mt-2">
              Ellenőrizve. Elintézve.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="text-white/55 text-sm sm:text-base mt-6 max-w-xl mx-auto leading-relaxed">
              Mi ellenőrizzük az autót, intézzük a szükséges dokumentációt és koordináljuk a magyarországi forgalomba helyezéshez szükséges ügyintézést. Ön pedig a rendelkezésére álló információk alapján dönthet.
            </p>
          </Reveal>
          <Reveal delay={0.34}>
            <Button onClick={() => scrollToSection("cars")} className="btn-gold rounded-full font-bold px-10 py-5 text-sm sm:text-base mt-9 inline-flex items-center gap-2" data-testid="closing-cta-btn">
              Autók böngészése <ArrowRight className="w-4 h-4" />
            </Button>
          </Reveal>
          <Reveal delay={0.42}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-white/45">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Átvizsgált járművek</span>
              <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D4AF37]" /> Ausztriai forrás</span>
              <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> Teljes körű ügyintézés</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-16 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8 sm:mb-12">
            <div className="sm:col-span-2">
              <div className="mb-4">
                <img src="/alpine-logo-full.png" alt="Alpine Car Austria" className="h-16 w-auto object-contain" />
              </div>
              <p className="text-white/40 text-sm max-w-sm">Ausztriából származó, gondosan átvizsgált autók. Mi ellenőrizzük a járművet és intézzük a szükséges ügyintézést.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-sm">Navigáció</h4>
              <div className="space-y-2 text-sm text-white/40">
                <button onClick={() => scrollToSection("cars")} className="block hover:text-[#D4AF37] transition-colors">Autók</button>
                <button onClick={() => scrollToSection("process")} className="block hover:text-[#D4AF37] transition-colors">Folyamat</button>
                <button onClick={() => scrollToSection("search")} className="block hover:text-[#D4AF37] transition-colors">Autót keresek</button>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-sm">Kapcsolat</h4>
              <div className="space-y-2 text-sm text-white/40">
                <p>+43 660 4428 540</p>
                <p>info@alpinecar.eu</p>
                <p>Traun, Ausztria</p>
                <div className="pt-2">
                  <WhatsAppGroupButton variant="footer" label="WhatsApp" showHours={false} testId="footer-wa-group" />
                </div>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-xs sm:text-sm">© 2026 Alpine Car</p>
            <a href="/admin" className="text-white/30 text-xs sm:text-sm hover:text-[#D4AF37] transition-colors">Admin</a>
          </div>
        </div>
      </footer>

      {/* WhatsApp Group Float */}
      <a href={waGroupUrl} target="_blank" rel="noopener noreferrer" className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl shadow-[#25D366]/30 hover:scale-110 transition-transform" data-testid="floating-wa-group">
        <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
      </a>
    </div>
  );
}
